import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ProductOtherLocationsDocument } from 'src/gql/graphql';
import useUI from 'src/hooks/use-ui';

export const OTHER_LOCATIONS_FLAG = 'growth.ecomm.pdp-other-locations.rollout';

/**
 * The resolver clamps to 50. Requesting exactly that means the count we show ("in stock at N
 * other locations") is exact for any chain up to 50 stocking stores, and we can tell when
 * we've been truncated and render "50+" instead of quietly under-reporting.
 */
const LOCATION_LIMIT = 50;

type ProductLike = {
  enterpriseProductId?: string | null;
};

type DispensaryLike = {
  id?: string | null;
  retailer?: { enterpriseId?: string | null } | null;
  storeSettings?: { enableOtherLocations?: boolean | null } | null;
};

export type OtherLocation = {
  dispensaryId: string;
  cName: string;
  name: string;
  address?: string | null;
  chain?: string | null;
  /** Straight-line miles from the store being viewed; null when either lacks coordinates. */
  distanceMiles?: number | null;
  productId: string;
  productUrl: string;
};

export type UseOtherLocationsReturn = {
  /** Nearest to the store being viewed first; locations without coordinates last. */
  locations: OtherLocation[];
  loading: boolean;
  enabled: boolean;
  /** True when the resolver hit its cap, so `locations` is a prefix of the real total. */
  capped: boolean;
};

type UseOtherLocationsParams = {
  product?: ProductLike | null;
  dispensary?: DispensaryLike | null;
};

/**
 * Other locations in the same enterprise that currently stock this product.
 *
 * A single `productOtherLocations` query does the whole join server-side. It deliberately
 * does not reuse `useEnterpriseProductAvailability`: that hook pulls the enterprise's entire
 * product catalogue plus a full consumer product payload for every sibling location, which is
 * far too much work for a list of store names.
 *
 * Runs only for enterprise products (`dispensary.retailer.enterpriseId` +
 * `product.enterpriseProductId`) when the retailer has opted in via the
 * `enableOtherLocations` store setting and the `growth.ecomm.pdp-other-locations.rollout`
 * flag is on. The flag controls platform rollout; the store setting is the retailer's own
 * choice, so both have to be true.
 *
 * Never runs on kiosk: a kiosk is locked to one dispensary, so linking a shopper to another
 * store's menu would break out of that session.
 */
export function useOtherLocations({ product, dispensary }: UseOtherLocationsParams): UseOtherLocationsReturn {
  const flags = useFlags();
  const UI = useUI();

  const flagEnabled = (flags[OTHER_LOCATIONS_FLAG] as boolean | undefined) ?? false;
  const settingEnabled = dispensary?.storeSettings?.enableOtherLocations ?? false;
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? undefined;
  const enterpriseProductId = product?.enterpriseProductId ?? undefined;
  const currentDispensaryId = dispensary?.id ?? undefined;

  const enabled = Boolean(
    flagEnabled && settingEnabled && !UI.isKiosk && enterpriseId && enterpriseProductId && currentDispensaryId
  );

  const { data, loading } = useQuery(ProductOtherLocationsDocument, {
    variables: {
      enterpriseId: enterpriseId ?? '',
      enterpriseProductId: enterpriseProductId ?? '',
      excludeDispensaryId: currentDispensaryId,
      limit: LOCATION_LIMIT,
    },
    skip: !enabled,
  });

  if (!enabled) {
    return { locations: [], loading: false, enabled: false, capped: false };
  }

  const locations = (data?.productOtherLocations ?? []).map((location) => ({
    dispensaryId: location.dispensaryId,
    cName: location.cName,
    name: location.name,
    address: location.address,
    chain: location.chain,
    distanceMiles: location.distanceMiles,
    productId: location.productId,
    productUrl: `/${UI.dispensaryRootUrl}/${location.cName}/product/${location.productId}`,
  }));

  return { locations, loading, enabled: true, capped: locations.length >= LOCATION_LIMIT };
}
